import React, { useState, useEffect, Suspense } from 'react';
import { Outlet, Link, useLocation, useNavigate, Navigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth, API } from '../context/AuthContext';
import { INVENTORY_CHANGED } from './inventory/inventoryEvents';
import { useFeatureFlags } from '../context/FeatureFlagContext';
import { useBranding } from '../context/BrandingContext';
import { moduleConfig, canAccessDashboard } from '../config/moduleConfig';
import useNavVisibility from '../hooks/useNavVisibility';
import PROCESSING_OVERVIEW_TABS from '../config/processingOverviewTabs';
import PARTY_WORKSPACE_TABS from '../config/partyWorkspaceTabs';
import INVENTORY_TABS from '../config/inventoryTabs';
import { getStageThemes } from '../utils/stageTheme';
import { Button } from './ui/button';
import AnnouncementBanner from './AnnouncementBanner';
import NotificationBell, { badgeOf } from './NotificationBell';
import { useMoney } from '../context/MoneyContext';
import GlobalSearch from './GlobalSearch';
import AssistantWidget from './AssistantWidget';
import MobileBottomNav from './MobileBottomNav';
import SupervisionHoldDialog from './supervision/SupervisionHoldDialog';
import StepTimesDialog from './shared/StepTimesDialog';
import SupervisionPulse from './supervision/SupervisionPulse';
import { NavigationProvider } from '../context/NavigationContext';
import { usePpPhaseMeta, PP_PHASES_CHANGED } from '../hooks/usePpPhaseMeta';
import { toast } from 'sonner';
import { setFocusMissHandler } from '../utils/recordFocus';
import { GOTO_PARAM, GOTO_TAB_PARAM, goToDestination } from '../utils/deepLink';
import { useNativeBack } from '../utils/nativeBack';
import useBreakpoint from '../hooks/useBreakpoint';
import { APPBAR_SLOT_ID, isNativeRoute } from '../config/nativeScreens';
import { installZeroSelect } from '../utils/zeroSelect';
import { installEnterNext } from '../utils/enterNext';
import { installKeyboardInset } from '../utils/keyboardInset';
import { 
  LayoutDashboard, 
  ShoppingCart, 
  Users, 
  Package, 
  Factory, 
  Box, 
  Briefcase,
  Bell,
  Eye,
  EyeOff,
  LogOut,
  Menu,
  X,
  ClipboardCheck,
  ClipboardList,
  Scissors,
  LayoutGrid,
  Snowflake,
  Ship,
  Receipt,
  Settings,
  TrendingDown,
  Target,
  Paperclip,
  History,
  Upload,
  DollarSign,
  FileText,
  Building2,
  BookOpen,
  Shield,
  ShieldCheck,
  CalendarClock,
  Bug,
  Ruler,
  TrendingUp,
  SlidersHorizontal,
  Scale,
  Clock3,
  ChevronDown,
  PanelLeftClose,
  PanelLeftOpen,
  ShoppingBag,
  Boxes,
  BarChart3,
  UserCheck,
  CalendarDays,
  Wallet,
  CreditCard,
  Tag,
  Layers,
  Warehouse,
  Workflow,
  Truck,
  FileSearch,
  Home,
  Calculator,
  CircleDollarSign,
  Sparkles,
  Timer,
  CircleHelp
} from 'lucide-react';

const SIDEBAR_GROUPS_STORAGE_KEY = 'erp_sidebar_collapsed_v1';
const SIDEBAR_DESKTOP_COLLAPSED_STORAGE_KEY = 'erp_sidebar_desktop_collapsed_v1';

const Layout = () => {
  const { mayShowMoney, moneyHidden, toggleMoney } = useMoney();
  const { user, logout, isImpersonating, endImpersonation } = useAuth();
  const { isEnabled, features, loading: featuresLoading } = useFeatureFlags();
  const { meta: ppMeta } = usePpPhaseMeta();
  const { canSee } = useNavVisibility();
  const { branding } = useBranding();
  const stageThemes = getStageThemes(branding.stage_colors);
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  // Android back closes the phone drawer before it leaves the page.
  useNativeBack(sidebarOpen, () => setSidebarOpen(false));
  const [desktopSidebarCollapsed, setDesktopSidebarCollapsed] = useState(() => {
    try { return localStorage.getItem(SIDEBAR_DESKTOP_COLLAPSED_STORAGE_KEY) === 'true'; } catch { return false; }
  });
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  const currentTenantId = user?.tenant_id || 'unknown';

  // Scroll back to the top of the page whenever the route changes, so clicking a
  // sidebar tab always lands the user at the top of the destination page.
  // Tap-to-replace for zero-valued number fields on the native floor screens.
  useEffect(() => installZeroSelect(), []);
  useEffect(() => installEnterNext(), []);
  useEffect(() => installKeyboardInset(), []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  // "Open the record" from an alert, a notification or a search hit hunts the
  // destination page for the row. When the hunt comes up empty the screen used to
  // sit there unchanged, which reads as a broken link. Say it plainly instead —
  // an alert that cannot show its record must admit that, not stay silent.
  // Search hits that name a tab or a panel rather than a record arrive as
  // `?goto=<heading>&gotoTab=<value>`. Handled here, once, for every page: a page
  // needs no code of its own to be deep-linkable into, which is the only way
  // "search everything" can stay true as screens are added. The params are stripped
  // straight away so a refresh or a back-navigation does not re-run the hunt.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const find = params.get(GOTO_PARAM);
    const tab = params.get(GOTO_TAB_PARAM);
    if (!find && !tab) return;
    params.delete(GOTO_PARAM);
    params.delete(GOTO_TAB_PARAM);
    const rest = params.toString();
    navigate(`${location.pathname}${rest ? `?${rest}` : ''}`, { replace: true });
    goToDestination({
      find,
      tab,
      onMiss: (what) => toast.warning(`Could not find "${what}" on this page`, {
        description: 'It may have been renamed, or it only appears once there is data to show.',
      }),
    });
  }, [location.search, location.pathname, navigate]);

  useEffect(() => setFocusMissHandler((keys) => {
    const name = keys[0];
    toast.warning(
      name ? `${name} is not available on this screen` : 'That record is not available here',
      { description: 'It may have been deleted, or it is not listed on this page. Try the search box at the top.' },
    );
  }), []);

  useEffect(() => {
    if (!user) {
      setUnreadNotifications(0);
      return undefined;
    }

    // Only what is waiting on this person — the same number as the bell.
    const handleUpdated = (event) => {
      setUnreadNotifications(badgeOf(event.detail));
    };

    window.addEventListener('notificationsUpdated', handleUpdated);
    return () => window.removeEventListener('notificationsUpdated', handleUpdated);
  }, [user]);

  // Inventory badges — how many decisions are waiting for this login on Requests
  // and Purchasing. Both counts are the server's (`awaiting_my_approval`), so a
  // login that approves nothing never sees a badge. Re-read on any inventory
  // change and on entering the section, never on a timer.
  const [navBadges, setNavBadges] = useState({});
  const requestsFlag = isEnabled('inventoryRequests');
  const posFlag = isEnabled('purchaseOrders');
  const inInventory = location.pathname.startsWith('/inventory');
  useEffect(() => {
    if (!user || (!requestsFlag && !posFlag)) { setNavBadges({}); return undefined; }
    let alive = true;
    const load = () => {
      Promise.allSettled([
        requestsFlag ? axios.get(`${API}/inventory/requests/summary`) : Promise.resolve(null),
        posFlag ? axios.get(`${API}/inventory/purchase-orders/meta`) : Promise.resolve(null),
      ]).then(([r, p]) => {
        if (!alive) return;
        setNavBadges({
          inventory: (r.value?.data?.awaiting_my_approval || 0) + (p.value?.data?.awaiting_my_approval || 0),
        });
      });
    };
    load();
    window.addEventListener(INVENTORY_CHANGED, load);
    return () => { alive = false; window.removeEventListener(INVENTORY_CHANGED, load); };
  }, [user, requestsFlag, posFlag, inInventory]);

  // Pre-processing badges — lots waiting at each station this login works, and lots
  // ready for Lot Check. Counted by the server with the lists' own rules. Teams on
  // the floor never press refresh, so it is re-read every minute while the tab is
  // visible, on focus, on navigation and after any phase save (PP_PHASES_CHANGED).
  const [ppBadges, setPpBadges] = useState({});
  const ppBadgeOn = !!ppMeta && ((ppMeta.phases || []).length > 0 || !!(ppMeta.can_check ?? ppMeta.manager));
  useEffect(() => {
    if (!user || !ppBadgeOn) { setPpBadges({}); return undefined; }
    let alive = true;
    const load = () => {
      if (document.visibilityState === 'hidden') return;
      axios.get(`${API}/pp-phases/pending-counts`).then((r) => {
        if (!alive) return;
        const d = r.data || {};
        setPpBadges({ pp_arrival: d.arrival || 0, pp_deheading: d.deheading || 0,
          pp_grading: d.grading || 0, pp_lot_check: d.lot_check || 0 });
      }).catch(() => {});
    };
    load();
    const t = setInterval(load, 60000);
    window.addEventListener(PP_PHASES_CHANGED, load);
    window.addEventListener('focus', load);
    return () => {
      alive = false; clearInterval(t);
      window.removeEventListener(PP_PHASES_CHANGED, load);
      window.removeEventListener('focus', load);
    };
  }, [user, ppBadgeOn, location.pathname]);
  const allBadges = { ...navBadges, ...ppBadges };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleEndImpersonation = () => {
    endImpersonation();
    navigate('/login');
  };

  const iconMap = {
    LayoutDashboard,
    ShoppingCart,
    Users,
    Package,
    Factory,
    Box,
    Briefcase,
    ClipboardCheck,
    ClipboardList,
    Scissors,
    LayoutGrid,
    Snowflake,
    Ship,
    Receipt,
    Settings,
    Bell,
    TrendingDown,
    Target,
    Paperclip,
    DollarSign,
    History,
    Upload,
    FileText,
    Building2,
    BookOpen,
    Shield,
    ShieldCheck,
    CalendarClock,
    Bug,
    Ruler,
  TrendingUp,
  SlidersHorizontal,
    Scale,
    Clock3,
    UserCheck,
    CalendarDays,
    Wallet,
    CreditCard,
    Tag,
    Layers,
    Warehouse,
    Workflow,
    Truck,
    FileSearch,
    Home,
    Calculator,
    CircleDollarSign,
    Sparkles,
    Timer,
    CircleHelp
  };

  // Module groups, in sidebar order. Each nav item declares which group it
  // belongs to (`group`); `child: true` renders it as an indented sub-page of
  // the parent module above it. `flat` groups render their items directly
  // without a collapsible section header.
  const GROUPS = [
    { key: 'procurement', label: 'Procurement', icon: ShoppingBag },
    { key: 'preprocessing', label: 'Pre-Processing', icon: Layers },
    { key: 'processing', label: 'Production', icon: Factory },
    // Quality is a plant function in its own right, rather than a step within
    // processing. It retains the Processing colour family to keep the existing
    // tenant stage-colour customization intact.
    { key: 'quality', label: 'Quality Checks', icon: ClipboardCheck, themeKey: 'processing' },
    { key: 'storage', label: 'Cold Store', icon: Boxes },
    { key: 'sales', label: 'Sales & Dispatch', icon: Ship, flat: true },
    { key: 'inventory', label: 'Inventory', icon: Package, flat: true },
    { key: 'finance', label: 'Accounts & HRM', icon: Receipt },
    { key: 'hr', label: 'HR & Payroll', icon: UserCheck },
    { key: 'analytics', label: 'Analytics', icon: BarChart3 },
    { key: 'reports', label: 'Reports', icon: FileText, flat: true },
    { key: 'masters', label: 'Masters', icon: Settings, flat: true },
    { key: 'admin', label: 'Admin', icon: Settings },
    { key: 'help', label: 'Help & Training', icon: CircleHelp, flat: true },
  ];
  const toneForGroup = (key) => stageThemes[key] || stageThemes.analytics;
  const tintedStyle = (tone) => ({
    backgroundColor: tone.bg,
    color: tone.text,
    boxShadow: `inset 0 0 0 1px ${tone.border}`,
  });

  const MASTER_PAGE_KEYS = [
    'masters',
    'preprocessing',
    'sizeGrades',
    'defectMasters',
    'analysisReport',
    'production',
    'packingStyles',
    'qcCheckpoints',
    'coldStorage',
    'costComponents',
    'yieldBenchmarks',
    'marketRates',
    'specMasters',
  ];

  // Each item has moduleKey (role check), featureCode (super-admin feature flag),
  // and group (sidebar section). child=true → indented sub-page; pin → ungrouped.
  const navigation = [
    { name: 'Dashboard', path: '/', icon: 'LayoutDashboard', moduleKey: 'dashboard', featureCode: 'dashboard', pin: 'top' },

    // ── Procurement ───────────────────────────────────────────────────────
    { name: 'Purchase Booking', path: '/purchase-booking', icon: 'CalendarClock', moduleKey: 'purchaseBooking', featureCode: 'purchaseBooking', group: 'procurement' },
    { name: 'Purchase Raw Material', path: '/purchase-invoices', icon: 'FileText', moduleKey: 'procurement', featureCode: 'purchaseInvoiceDashboard', group: 'procurement' },
    // Use `admin` feature so access matches Admin Panel; do not gate on risk_comments_v2 (tenant DB can have it off).
    { name: 'Purchase Risk Alerts', path: '/purchase-risk-alerts', icon: 'Shield', moduleKey: 'admin', featureCode: 'admin', group: 'procurement' },
    // Party Master and Party Ledger were two entries about the same people — their
    // details on one screen, their money on another. They are one page of tabs now
    // (pages/Parties.js), which also holds Agents and Graders. Each tab is gated as
    // its entry was, so the row shows when any one of them is reachable — and no
    // single flag speaks for the page, which is why its route is left ungated below.
    { name: 'Parties & Ledger', path: '/parties', icon: 'Users', moduleKey: 'parties', anyOf: PARTY_WORKSPACE_TABS, group: 'procurement' },

    { name: 'Vehicle Compliance', path: '/vehicle-compliance', icon: 'Truck', moduleKey: 'vehicleCompliance', featureCode: 'vehicleCompliance', group: 'procurement' },

    // ── Pre-Processing ────────────────────────────────────────────────────
    // Its own stage: production days arriving (Overview), the raw-material pool,
    // the pre-processing screen and the screens hanging off it.
    // Overview and the Workflow Board now live under the Dashboard's Overview
    // tab. They remain feature- and role-gated there, rather than appearing as
    // another sidebar destination.
    // Material waiting to be picked into a lot is a stage of its own, not part of
    // recording pre-processing — so the pool is its own page, not a tab.
    { name: 'Plant Raw Material Entry', path: '/raw-material-pool', icon: 'Warehouse', moduleKey: 'rmMaterialAvailable', featureCode: 'rmMaterialAvailable', group: 'preprocessing', ppHideBlind: ['procurement'] },
    // With phase teams on (ppPhaseEntry) the stage is laid out the way the floor
    // runs it: the supervisor opens the production day and its lots, three teams
    // each key their own station blind, and the manager compares their weights in
    // Lot Check and sends the lot to Production. One screen per team, one gate —
    // the older combined screens below are hidden rather than offered beside it.
    { name: 'Production Days', path: '/procurement-batches', icon: 'Layers', moduleKey: 'procurementBatches', featureCode: 'ppPhaseEntry', group: 'preprocessing', ppHideBlind: true },
    { name: 'Arrival', path: '/preprocessing/phases/arrival', icon: 'Scale', moduleKey: 'preprocessing', featureCode: 'ppPhaseEntry', group: 'preprocessing', ppPhase: 'arrival', badge: 'pp_arrival', badgeTone: 'red' },
    { name: 'Deheading', path: '/preprocessing/phases/deheading', icon: 'Scissors', moduleKey: 'preprocessing', featureCode: 'ppPhaseEntry', group: 'preprocessing', ppPhase: 'deheading', badge: 'pp_deheading', badgeTone: 'red' },
    { name: 'Grading', path: '/preprocessing/phases/grading', icon: 'LayoutGrid', moduleKey: 'preprocessing', featureCode: 'ppPhaseEntry', group: 'preprocessing', ppPhase: 'grading', badge: 'pp_grading', badgeTone: 'red' },
    { name: 'Lot Check', path: '/preprocessing/lot-check', icon: 'ClipboardCheck', moduleKey: 'preprocessing', featureCode: 'ppPhaseEntry', group: 'preprocessing', ppManager: true, badge: 'pp_lot_check', badgeTone: 'red' },
    // Without phase teams: the combined entry screen and the pages hanging off it.
    { name: 'Pre-Processing Entry', path: '/preprocessing', icon: 'Package', moduleKey: 'preprocessing', featureCode: 'preprocessing', group: 'preprocessing', hideWhenFeature: 'ppPhaseEntry' },
    { name: 'Pre-Processing Home', path: '/preprocessing/home', icon: 'Home', moduleKey: 'preprocessingHome', featureCode: 'preprocessingHome', group: 'preprocessing', child: true, hideWhenFeature: 'ppPhaseEntry' },
    { name: 'Pre-Processing Workflow', path: '/preprocessing/workflow', icon: 'Workflow', moduleKey: 'preProcessingWorkflow', featureCode: 'preProcessingWorkflow', group: 'preprocessing', child: true, hideWhenFeature: 'ppPhaseEntry' },
    // Pre-Production Masters moved to the dedicated Masters module; legacy routes
    // still work if deep-linked. With phase teams on, Floor Balance is a tab of Lot Check.
    { name: 'Floor Balance', path: '/preprocessing/floor-balance', icon: 'Scale', moduleKey: 'floorBalance', featureCode: 'floorBalance', group: 'preprocessing', child: true, hideWhenFeature: 'ppPhaseEntry' },
    // ── Processing ────────────────────────────────────────────────────────
    { name: 'Batch Pipeline', path: '/batch-pipeline', icon: 'Workflow', moduleKey: 'batchPipeline', featureCode: 'batchPipeline', group: 'processing' },
    // Production Entry stands on its own now that the Production summary is a tab
    // on Overview; the legacy per-phase board nests under it.
    { name: 'Production Entry', path: '/production/entry', icon: 'ClipboardList', moduleKey: 'production', featureCode: 'productionDetailEntry', group: 'processing' },
    // Superseded by the unified Workflow Board. Gated on its own flag (off by default) so
    // retiring the board does not switch off the traveler engine underneath production.
    { name: 'Production Workflow', path: '/production/workflow', icon: 'Workflow', moduleKey: 'productionWorkflowBoard', featureCode: 'productionWorkflowBoard', group: 'processing', child: true },
    // ── Quality Checks ────────────────────────────────────────────────────
    // Quality is its own plant function. Shipment approval and factory-system
    // documentation stay at their decision points in Sales and Cold Store.
    { name: 'Quality Checks', path: '/qc', icon: 'ClipboardCheck', moduleKey: 'qc', featureCode: 'qualityControl', group: 'quality' },
    { name: 'Lab Test Results', path: '/qc/lab', icon: 'ClipboardCheck', moduleKey: 'qcLab', featureCode: 'qcStructured', group: 'quality', child: true },
    { name: 'Process Checkpoints', path: '/qc/gates', icon: 'ShieldCheck', moduleKey: 'qcCheckpoints', featureCode: 'qcCheckpoints', group: 'quality', child: true },
    { name: 'Online Inspection', path: '/qc/uniformity', icon: 'Ruler', moduleKey: 'onlineInspection', featureCode: 'onlineInspection', group: 'quality', child: true },
    { name: 'Uniformity Monitor', path: '/qc/uniformity/dashboard', icon: 'TrendingUp', moduleKey: 'uniformityDashboard', featureCode: 'uniformityControl', group: 'quality', child: true },
    { name: 'Inspection Masters', path: '/qc/uniformity/masters', icon: 'SlidersHorizontal', moduleKey: 'uniformityMasters', featureCode: 'onlineInspection', group: 'quality', child: true },

    // ── Cold Store ────────────────────────────────────────────────────────
    { name: 'Cold Storage Insights', path: '/cold-storage', icon: 'Snowflake', moduleKey: 'coldStorage', featureCode: 'coldStorage', group: 'storage' },
    { name: 'Cold Store Intake', path: '/cold-storage/intake', icon: 'Package', moduleKey: 'coldStorage', featureCode: 'coldStorage', group: 'storage' },
    // The cold-storage lifecycle board is now the last band of the unified Workflow
    // Board, and the chamber map is a tab on Cold Storage Insights — neither has a
    // nav entry of its own any more.
    { name: 'Cold Store Outflow', path: '/cold-storage/movements', icon: 'Package', moduleKey: 'storeMovements', featureCode: 'storeMovements', group: 'storage' },
    { name: 'Finished Goods', path: '/finished-goods', icon: 'Box', moduleKey: 'finishedGoods', featureCode: 'finishedGoods', group: 'storage' },
    { name: 'Merchants', path: '/merchants', icon: 'Briefcase', moduleKey: 'merchantProcessing', featureCode: 'merchantProcessing', group: 'storage' },
    { name: 'Client Intelligence', path: '/clients/intelligence', icon: 'Users', moduleKey: 'clientIntelligence', featureCode: 'clientIntelligence', group: 'storage' },
    { name: 'EIA Certificate', path: '/qc/eia', icon: 'FileText', moduleKey: 'eiaDocumentation', featureCode: 'eiaDocumentation', group: 'storage', child: true },

    // ── Sales & Dispatch ─────────────────────────────────────────────────
    { name: 'Sales & Dispatch', path: '/sales', icon: 'Ship', moduleKey: 'sales', featureCode: 'sales', group: 'sales' },
    { name: 'Shipment QC Release', path: '/qc/compliance', icon: 'ShieldCheck', moduleKey: 'qcCompliance', featureCode: 'qualityControl', group: 'sales' },

    // ── Inventory ─────────────────────────────────────────────────────────
    // One entry: the whole store is one page of tabs (pages/Inventory.js — Stock,
    // Indents, Purchase, Vendors, Costing). Each tab is gated as its old entry was,
    // so the row shows when any one of them is reachable; the badge is every
    // inventory decision waiting for this login.
    { name: 'Inventory', path: '/inventory', icon: 'Boxes', moduleKey: 'inventoryItems', anyOf: INVENTORY_TABS, group: 'inventory', badge: 'inventory' },

    // ── Finance ───────────────────────────────────────────────────────────
    { name: 'Control Room', path: '/accounts', icon: 'Receipt', moduleKey: 'accounts', featureCode: 'accounts', group: 'finance' },

    // ── HR & Payroll ──────────────────────────────────────────────────────
    { name: 'Employees', path: '/hr/employees', icon: 'UserCheck', moduleKey: 'hrEmployees', featureCode: 'hrEmployees', group: 'finance', child: true },
    { name: 'Attendance', path: '/hr/attendance', icon: 'CalendarDays', moduleKey: 'hrAttendance', featureCode: 'hrAttendance', group: 'finance', child: true },
    { name: 'Payroll', path: '/hr/payroll', icon: 'Wallet', moduleKey: 'hrPayroll', featureCode: 'hrPayroll', group: 'finance', child: true },
    { name: 'Expenses', path: '/hr/expenses', icon: 'CreditCard', moduleKey: 'hrExpenses', featureCode: 'hrExpenses', group: 'finance', child: true },

    // ── Analytics ─────────────────────────────────────────────────────────
    { name: 'Reverse RM Calculator', path: '/analytics/reverse-rm', icon: 'Target', moduleKey: 'reverseCalculator', featureCode: 'reverseCalculator', group: 'analytics' },
    { name: 'RM Requirement', path: '/analytics/rm-requirement', icon: 'ClipboardCheck', moduleKey: 'rmRequirement', featureCode: 'rmRequirementPlanning', group: 'analytics' },
    { name: 'Production Day P&L', path: '/analytics/lot-pnl', icon: 'DollarSign', moduleKey: 'lotPnl', featureCode: 'lotProfitability', group: 'analytics' },
    { name: 'Cost Analyser', path: '/analytics/cost-analyser', icon: 'Calculator', moduleKey: 'costAnalyser', featureCode: 'costAnalyser', group: 'analytics' },
    { name: 'Margin Command Center', path: '/analytics/margin-command-center', icon: 'CircleDollarSign', moduleKey: 'marginCommandCenter', featureCode: 'marginCommandCenter', group: 'analytics' },
    { name: 'Predicted Yield', path: '/analytics/yield-accuracy', icon: 'Sparkles', moduleKey: 'yieldAccuracy', featureCode: 'yieldPrediction', group: 'analytics' },
    { name: 'Cycle Times', path: '/analytics/cycle-times', icon: 'Timer', moduleKey: 'cycleTimes', featureCode: 'cycleTime', group: 'analytics' },
    { name: 'Grade Mix Calculator', path: '/analytics/grade-mix-calculator', icon: 'Calculator', moduleKey: 'gradeMixCalculator', featureCode: 'gradeMixChart', group: 'analytics' },
    { name: 'Purchase vs Production', path: '/analytics/purchase-vs-production', icon: 'Calculator', moduleKey: 'purchaseVsProduction', featureCode: 'gradeMixChart', group: 'analytics' },
    { name: 'Price Benchmarking', path: '/analytics/price-benchmarking', icon: 'Tag', moduleKey: 'priceBenchmarking', featureCode: 'priceBenchmarking', group: 'analytics' },
    { name: 'Production Day Lifecycle', path: '/analytics/lot-lifecycle', icon: 'Clock3', moduleKey: 'lotLifecycle', featureCode: 'lotLifecycle', group: 'analytics' },
    { name: 'Production Day Board', path: '/analytics/lot-board', icon: 'Layers', moduleKey: 'lotBoard', featureCode: 'lotBoard', group: 'analytics' },
    { name: 'Traceability', path: '/traceability', icon: 'FileSearch', moduleKey: 'traceability', featureCode: 'traceability', group: 'analytics' },
    { name: 'Wastage Dashboard', path: '/admin/wastage-dashboard', icon: 'TrendingDown', moduleKey: 'wastageDashboard', featureCode: 'wastageDashboard', group: 'analytics' },
    // ── Reports ───────────────────────────────────────────────────────────
    { name: 'Reports', path: '/reports', icon: 'FileText', moduleKey: 'reports', featureCode: 'reports', group: 'reports' },

    // ── Masters ───────────────────────────────────────────────────────────
    { name: 'Masters', path: '/masters', icon: 'Settings', moduleKey: 'masters', group: 'masters' },

    // ── Administration ────────────────────────────────────────────────────
    { name: 'Admin Panel', path: '/admin', icon: 'Settings', moduleKey: 'admin', featureCode: 'admin', group: 'admin' },
    { name: 'Audit Trail', path: '/admin/audit-trail', icon: 'History', moduleKey: 'auditTrail', featureCode: 'auditTrail', group: 'admin' },
    { name: 'Data Migration', path: '/data-migration', icon: 'Upload', moduleKey: 'dataMigration', featureCode: 'dataMigration', group: 'admin' },
    { name: 'Factory QC Audits', path: '/qc/audits', icon: 'ClipboardList', moduleKey: 'qcAudits', featureCode: 'qcAudits', group: 'admin', child: true },

    // Gated on the `supervision` flag but on no role: the maker's own view.
    { name: 'My Change Requests', path: '/my-change-requests', icon: 'ShieldCheck', moduleKey: 'myChangeRequests', featureCode: 'supervision', pin: 'bottom' },
    { name: 'Notifications', path: '/notifications', icon: 'Bell', moduleKey: 'notifications', featureCode: 'notifications', pin: 'bottom' },
    { name: 'Docs & Guided Help', path: '/docs', icon: 'CircleHelp', moduleKey: 'docs', featureCode: 'docs', pin: 'bottom' },
  ];

  // Path → feature code: redirect if user opens a URL for a disabled feature
  const pathToFeature = {
    '/': 'dashboard',
    // /procurement is a legacy redirect into the Dashboard Overview workspace.
    // It stays ungated so old bookmarks can always complete their redirect.
    '/procurement': null,
    // Spelled out because '/procurement' above is now null and prefix-matching
    // would otherwise hand this page that null and leave it ungated. 'procurement'
    // is the flag it already resolved to through that prefix — not the
    // off-by-default 'procurementBatches', which would newly block the page.
    '/procurement-batches': 'procurement',
    '/purchase-booking': 'purchaseBooking',
    '/agents': 'agents',
    '/preprocessing': 'preprocessing',
    '/raw-material-pool': 'rmMaterialAvailable',
    '/preprocessing/defect-masters': 'preprocessingDefects',
    '/preprocessing/size-grades': 'countGrading',
    '/preprocessing/grading': 'gradingFeedsProduction',
    '/preprocessing/phases': 'ppPhaseEntry',
    '/preprocessing/reconcile': 'ppPhaseEntry',
    '/preprocessing/lot-check': 'ppPhaseEntry',
    '/preprocessing/floor-balance': 'floorBalance',
    // Both legacy per-phase boards — off by default, and a direct URL now redirects
    // rather than rendering a screen the nav no longer offers.
    '/preprocessing/workflow': 'preProcessingWorkflow',
    '/production/workflow': 'productionWorkflowBoard',
    '/production': 'production',
    '/production/packing-styles': 'fgPacking',
    // Now a redirect into Reports → HON-to-Finished Analysis. Kept here so the legacy
    // URL is still gated: with the flag off it redirects out rather than bouncing to a
    // Reports tab that no longer carries the card.
    '/analytics/hon-to-finished': 'honToFinished',
    '/analytics/reverse-rm': 'reverseCalculator',
    '/analytics/form-yields': 'reverseCalculator',
    '/analytics/spec-masters': 'productSpecDimensions',
    '/analytics/rm-requirement': 'rmRequirementPlanning',
    '/analytics/lot-pnl': 'lotProfitability',
    '/analytics/cost-analyser': 'costAnalyser',
    '/analytics/cost-components': 'costAnalyser',
    '/analytics/procurement-alerts': 'procurementAlerts',
    '/analytics/lot-lifecycle': 'lotLifecycle',
    '/analytics/margin-command-center': 'marginCommandCenter',
    '/analytics/yield-accuracy': 'yieldPrediction',
    '/analytics/cycle-times': 'cycleTime',
    '/analytics/grade-mix-calculator': 'gradeMixChart',
    '/analytics/purchase-vs-production': 'gradeMixChart',
    '/traceability': 'traceability',
    '/qc': 'qualityControl',
    '/qc/lab': 'qcStructured',
    '/qc/gates': 'qcCheckpoints',
    '/qc/masters': 'qcCheckpoints',
    '/qc/uniformity': 'onlineInspection',
    '/qc/uniformity/dashboard': 'uniformityControl',
    '/qc/uniformity/masters': 'onlineInspection',
    '/qc/compliance': 'qualityControl',
    '/qc/audits': 'qcAudits',
    '/qc/eia': 'eiaDocumentation',
    '/plant-overview': 'plantOverview',
    '/cold-storage': 'coldStorage',
    '/cold-storage/intake': 'coldStorage',
    // /cold-storage/store-view, /cold-storage/workflow and /workflow are redirects
    // now — left unguarded on purpose so the hop always runs and the destination
    // does the gating. `/production` stays listed because it still guards the
    // sub-pages that prefix-match it (/production/entry, /production/masters).
    '/cold-storage/masters': 'coldStorage',
    '/cold-storage/movements': 'storeMovements',
    // /inventory is one page of tabs and no single flag speaks for it (a tenant can
    // run Vendors without Materials Inventory), so it is left unguarded: the page
    // gates each tab. Every other /inventory/* path is a redirect into it.
    '/finished-goods': 'finishedGoods',
    '/merchants': 'merchantProcessing',
    '/sales': 'sales',
    '/accounts': 'accounts',
    '/purchase-invoices': 'purchaseInvoiceDashboard',
    '/purchase-risk-alerts': 'admin',
    // Parties & Ledger is a page of tabs with two independent flags behind it, so
    // it gates itself (see pages/Parties.js) exactly as /procurement does.
    // /party-ledger is a redirect into it and is left unguarded so the hop always
    // runs and the destination does the gating.
    '/parties': null,
    '/data-migration': 'dataMigration',
    '/hr/employees': 'hrEmployees',
    '/hr/attendance': 'hrAttendance',
    '/hr/payroll': 'hrPayroll',
    '/hr/expenses': 'hrExpenses',
    '/analytics/lot-board': 'lotBoard',
    '/admin/wastage-dashboard': 'wastageDashboard',
    '/admin/yield-benchmarks': 'yieldBenchmarks',
    '/admin/market-rates': 'marketRates',
    '/admin/audit-trail': 'auditTrail',
    '/admin/company-settings': 'admin',
    '/masters': null,
    '/admin': 'admin',
    '/notifications': 'notifications',
    '/my-change-requests': 'supervision',
  };
  const pathname = location.pathname;
  const normalized = pathname.replace(/\/$/, '') || '/';
  // The floor screens get the Material phone layout (config/nativeScreens.js).
  // `m3-page` only does anything below md — see the phone block in index.css.
  const { isPhone } = useBreakpoint();
  const nativeScreen = isPhone && isNativeRoute(normalized);
  const matchedKey = pathToFeature[normalized] != null
    ? normalized
    : Object.keys(pathToFeature)
        .filter((k) => k !== '/' && pathname.startsWith(k))
        .sort((a, b) => b.length - a.length)[0];
  const featureForPath = matchedKey ? pathToFeature[matchedKey] : null;
  const hasFeatures = Object.keys(features).length > 0;
  // Gate is active once we have any flag data (from event, cache, or /auth/me).
  // When featuresLoading=true and hasFeatures=false we're mid-load — treat as gate active to
  // prevent flashing all tabs. Super admin bypasses gating entirely.
  const flagsGateActive = hasFeatures || (featuresLoading && !hasFeatures);
  const featureBlocked =
    flagsGateActive &&
    featureForPath &&
    !isEnabled(featureForPath) &&
    user?.role !== 'super_admin';

  // Filter navigation. The precedence lives in useNavVisibility so the Processing
  // Overview tabs — which stand in for three nav entries that used to be here —
  // reach the same verdict rather than re-implementing it.
  const visibleNav = navigation.filter((item) => {
    if (item.moduleKey === 'dashboard') {
      if (!canAccessDashboard(user?.role)) return false;
      if (item.featureCode && flagsGateActive && !isEnabled(item.featureCode)) return false;
      return true;
    }
    // Masters stands for a set of pages: holding any one of them is enough.
    if (item.moduleKey === 'masters') return canSee({ ...item, pageKeys: MASTER_PAGE_KEYS });
    if (item.hideWhenFeature && isEnabled(item.hideWhenFeature)) return false;
    // Phase teams: a station is offered to the people who key it, Lot Check to the
    // manager, and a blind team member is not offered the lot-wide screens at all.
    if ((item.ppPhase || item.ppManager || item.ppHideBlind) && isEnabled('ppPhaseEntry')) {
      if (!ppMeta) return false;
      if (item.ppPhase && !(ppMeta.phases || []).includes(item.ppPhase)) return false;
      if (item.ppPhase === 'grading' && ppMeta.grading_on === false) return false;
      if (item.ppManager && !(ppMeta.can_check ?? ppMeta.manager)) return false;
      if (item.ppHideBlind && ppMeta.blind) {
        const allow = Array.isArray(item.ppHideBlind) ? item.ppHideBlind : [];
        if (!(ppMeta.stages || []).some((st) => allow.includes(st))) return false;
      }
    }
    return canSee(item);
  });

  // The bell and the full Notifications tab share the same tenant feature flag.
  const notificationsVisible = !flagsGateActive || isEnabled('notifications') || user?.role === 'super_admin';

  // ── Sidebar grouping ──────────────────────────────────────────────────────
  const isItemActive = (item) => {
    if (normalized === item.path) return true;
    if (item.path === '/') return false;

    const hasMoreSpecificVisibleMatch = visibleNav.some((other) =>
      other.path !== item.path &&
      other.path !== '/' &&
      (normalized === other.path || normalized.startsWith(other.path + '/'))
    );

    return !hasMoreSpecificVisibleMatch && normalized.startsWith(item.path + '/');
  };

  const pinnedTop = visibleNav.filter((i) => i.pin === 'top');
  const pinnedBottom = visibleNav.filter((i) => i.pin === 'bottom');
  // Build groups in declared order, dropping any that end up empty after filtering.
  const groupedNav = GROUPS
    .map((g) => ({ ...g, items: visibleNav.filter((i) => i.group === g.key) }))
    .filter((g) => g.items.length > 0);
  const sidebarLabel = branding.sidebar_label || 'Prawn ERP';
  const sidebarInitials = sidebarLabel
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase() || 'PE';
  const sidebarWidthClass = desktopSidebarCollapsed ? 'lg:w-[76px]' : 'lg:w-[280px]';

  // Collapse state persists per user; the group holding the active route is
  // always forced open so the current page is never hidden.
  const [collapsedGroups, setCollapsedGroups] = useState(() => {
    try { return JSON.parse(localStorage.getItem(SIDEBAR_GROUPS_STORAGE_KEY)) || {}; } catch { return {}; }
  });
  const toggleGroup = (key) =>
    setCollapsedGroups((prev) => {
      const next = { ...prev, [key]: !prev[key] };
      try { localStorage.setItem(SIDEBAR_GROUPS_STORAGE_KEY, JSON.stringify(next)); } catch { /* ignore */ }
      return next;
    });

  const toggleDesktopSidebar = () => {
    setDesktopSidebarCollapsed((prev) => {
      const next = !prev;
      try { localStorage.setItem(SIDEBAR_DESKTOP_COLLAPSED_STORAGE_KEY, String(next)); } catch { /* ignore */ }
      return next;
    });
  };

  // The tablet rail has no room to expand in place, so a group tapped there opens
  // the full drawer over the page with that group unfolded.
  const openDrawerGroupFromRail = (key) => {
    setSidebarOpen(true);
    setCollapsedGroups((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev, [key]: false };
      try { localStorage.setItem(SIDEBAR_GROUPS_STORAGE_KEY, JSON.stringify(next)); } catch { /* ignore */ }
      return next;
    });
  };

  const openSidebarGroupFromRail = (key) => {
    setDesktopSidebarCollapsed(false);
    try { localStorage.setItem(SIDEBAR_DESKTOP_COLLAPSED_STORAGE_KEY, 'false'); } catch { /* ignore */ }
    setCollapsedGroups((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev, [key]: false };
      try { localStorage.setItem(SIDEBAR_GROUPS_STORAGE_KEY, JSON.stringify(next)); } catch { /* ignore */ }
      return next;
    });
  };

  const renderNavLink = (item, { compact = false } = {}) => {
    const Icon = iconMap[item.icon];
    const isActive = isItemActive(item);
    const indented = item.child || item.grandchild;
    const group = GROUPS.find((g) => g.key === item.group);
    const tone = toneForGroup(group?.themeKey || group?.key || 'analytics');

    if (compact) {
      return (
        <Link
          key={item.path}
          to={item.path}
          onClick={() => setSidebarOpen(false)}
          title={item.name}
          aria-label={item.name}
          className={`
            relative mx-auto flex h-11 w-11 items-center justify-center rounded-xl transition-all duration-150
            ${isActive
              ? ''
              : group?.flat ? 'opacity-80 hover:opacity-100' : 'text-slate-500 hover:bg-slate-100 hover:text-slate-900'}
          `}
          style={(isActive || group?.flat) ? tintedStyle(tone) : undefined}
          data-testid={`nav-${item.name.toLowerCase().replace(/ /g, '-')}`}
        >
          {Icon && <Icon size={20} strokeWidth={2.2} />}
          {item.badge && allBadges[item.badge] > 0 && (
            <span className={`absolute right-1.5 top-1.5 h-2.5 w-2.5 rounded-full border-2 border-white ${item.badgeTone === 'red' ? 'bg-rose-500' : 'bg-violet-600'}`} />
          )}
          {item.path === '/notifications' && unreadNotifications > 0 && (
            <span
              className="absolute right-1.5 top-1.5 h-2.5 w-2.5 rounded-full border-2 border-white bg-rose-500"
              title={`${unreadNotifications} notifications need your action`}
            />
          )}
        </Link>
      );
    }

    return (
      <Link
        key={item.path}
        to={item.path}
        onClick={() => setSidebarOpen(false)}
        className={`
          group relative flex min-h-10 items-center gap-3 rounded-lg font-medium transition-all duration-150
          ${item.grandchild ? 'ml-7 pl-3 pr-3 py-2 text-[12.5px]' : item.child ? 'ml-4 pl-3 pr-3 py-2 text-[13px]' : 'px-3 py-2.5 text-sm'}
          ${isActive
            ? 'font-semibold'
            : indented ? 'hover:bg-slate-100 hover:text-slate-900' : 'hover:bg-slate-100 hover:text-slate-900'}
        `}
        style={isActive ? tintedStyle(tone) : group ? { color: '#475569' } : undefined}
        data-testid={`nav-${item.name.toLowerCase().replace(/ /g, '-')}`}
      >
        {/* The accent bar rides *outside* the pill, on the group's guide line, so
            "which section" and "which page" are two separate marks rather than
            one ambiguous highlight. */}
        <span
          className="absolute -left-2 top-1.5 bottom-1.5 w-1 rounded-full transition-colors"
          style={{ backgroundColor: isActive ? tone.color : 'transparent' }}
        />
        {Icon && (
          <Icon
            size={indented ? 16 : 18}
            strokeWidth={2}
            className="shrink-0 transition-colors"
            style={{ color: isActive ? tone.text : group ? '#94a3b8' : undefined }}
          />
        )}
        <span className="truncate">{item.name}</span>
        {item.badge && allBadges[item.badge] > 0 && (
          <span className={`ml-auto rounded-full px-1.5 py-0.5 text-[10px] font-bold tabular-nums text-white ${item.badgeTone === 'red' ? 'bg-rose-500' : 'bg-violet-600'}`}
            title={item.badgeTone === 'red' ? `${allBadges[item.badge]} lot(s) waiting` : `${allBadges[item.badge]} waiting for your approval`}>
            {allBadges[item.badge] > 99 ? '99+' : allBadges[item.badge]}
          </span>
        )}
        {item.path === '/notifications' && unreadNotifications > 0 && (
          <span
            className={`ml-auto rounded-full px-1.5 py-0.5 text-[10px] font-bold tabular-nums ${
              isActive ? 'bg-indigo-600 text-white' : 'bg-rose-500 text-white'
            }`}
            title={`${unreadNotifications} notifications need your action`}
          >
            {unreadNotifications > 99 ? '99+' : unreadNotifications}
          </span>
        )}
      </Link>
    );
  };

  const renderExpandedGroup = (g) => {
    if (g.flat) {
      return (
        <div key={g.key} className="space-y-1 pt-2">
          {g.items.map((item) => renderNavLink(item))}
        </div>
      );
    }

    const GroupIcon = g.icon;
    const groupHasActive = g.items.some(isItemActive);
    const isOpen = !collapsedGroups[g.key] || groupHasActive;
    const tone = toneForGroup(g.themeKey || g.key);

    return (
      <div key={g.key} className="pt-3">
        <button
          type="button"
          onClick={() => toggleGroup(g.key)}
          className={`
            flex w-full items-center gap-2 rounded-lg px-2.5 py-2.5 text-[11px] font-bold uppercase tracking-[0.09em] transition-all
          `}
          style={{
            color: tone.text,
            backgroundColor: tone.bg,
            boxShadow: groupHasActive
              ? `inset 3px 0 0 0 ${tone.color}, inset 0 0 0 1px ${tone.border}`
              : `inset 0 0 0 1px ${tone.border}`,
          }}
          aria-expanded={isOpen}
          data-testid={`nav-group-${g.key}`}
        >
          {GroupIcon && (
            <span
              className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg transition-colors"
              style={tintedStyle(tone)}
            >
              <GroupIcon size={15} strokeWidth={2.2} />
            </span>
          )}
          <span className="min-w-0 flex-1 truncate text-left">{g.label}</span>
          <ChevronDown
            size={15}
            className={`shrink-0 transition-transform duration-200 ${isOpen ? '' : '-rotate-90'}`}
            style={{ color: tone.text }}
          />
        </button>
        {isOpen && (
          <div className="mt-1 space-y-1 border-l pl-3 transition-colors" style={{ borderColor: tone.border }}>
            {g.items.map((item) => renderNavLink(item))}
          </div>
        )}
      </div>
    );
  };

  const renderRailGroupButton = (g, onOpen = openSidebarGroupFromRail) => {
    const GroupIcon = g.icon;
    const groupHasActive = g.items.some(isItemActive);
    const tone = toneForGroup(g.themeKey || g.key);

    if (g.flat && g.items.length === 1) {
      return renderNavLink(g.items[0], { compact: true });
    }

    return (
      <button
        key={g.key}
        type="button"
        onClick={() => onOpen(g.key)}
        title={g.label}
        aria-label={g.label}
        className={`
          relative mx-auto flex h-11 w-11 items-center justify-center rounded-xl transition-all duration-150
          ${groupHasActive
            ? ''
            : 'opacity-80 hover:opacity-100'}
        `}
        style={tintedStyle(tone)}
        data-testid={`nav-group-${g.key}`}
      >
        {GroupIcon && <GroupIcon size={20} strokeWidth={2.2} />}
        {groupHasActive && (
          <span className="absolute -left-2 top-1/2 h-6 w-1 -translate-y-1/2 rounded-r-full" style={{ backgroundColor: tone.color }} />
        )}
      </button>
    );
  };

  return (
    <NavigationProvider items={visibleNav} groups={GROUPS}>
    <div className="min-h-screen bg-slate-50">
      {/* Impersonation Banner */}
      {isImpersonating && (
        <div className="bg-purple-600 text-white px-4 py-2 flex items-center justify-between">
          <div className="flex items-center">
            <span className="font-medium">
              You are impersonating this account
            </span>
            {user?.impersonator_name && (
              <span className="ml-2 text-purple-200">
                (as {user.impersonator_name})
              </span>
            )}
          </div>
          <button
            onClick={handleEndImpersonation}
            className="px-3 py-1 bg-white text-purple-600 rounded text-sm font-medium hover:bg-purple-100"
            data-testid="end-impersonation-btn"
          >
            End Impersonation
          </button>
        </div>
      )}
      
      {/* The sidebar is a column of the page, not something tucked under the top
          bar: it starts at the very top so the company logo sits flush in the
          corner with no dead space above it. The banner + top bar therefore live
          inside the content column, to the right of it. */}
      <div className="flex min-w-0 items-start">
        {/* Sidebar */}
        {/* Off-canvas drawer below lg, sticky full-height column from lg up.
            The drawer covers the whole viewport height and sits above the top bar
            rather than being offset below it — the old fixed 57px offset was wrong
            whenever an impersonation or announcement banner pushed the bar down. */}
        <aside
          className={`
            fixed lg:sticky lg:top-0 inset-y-0 left-0 z-50 lg:z-30 flex h-screen w-80 max-w-[88vw] flex-col
            border-r border-slate-200 bg-white shadow-xl shadow-slate-900/10 lg:max-w-none lg:shadow-none
            transform transition-[transform,width] duration-200 ease-in-out ${sidebarWidthClass}
            ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
          `}
          style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}
          data-testid="sidebar"
        >
          <div className="flex shrink-0 items-center justify-between border-b border-slate-200 px-4 py-3 lg:hidden">
            <span className="truncate text-sm font-semibold text-slate-800">
              {sidebarLabel}
            </span>
            <button
              type="button"
              onClick={() => setSidebarOpen(false)}
              className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
              aria-label="Close menu"
            >
              <X size={20} />
            </button>
          </div>
          <div className={`${desktopSidebarCollapsed ? 'lg:hidden' : 'lg:flex'} flex min-h-0 flex-1 flex-col`}>
            {/* Same height as the top bar next to it, so the logo lines up with the
                header row instead of floating in a band of its own. */}
            <div className="hidden h-[61px] shrink-0 items-center justify-between border-b border-slate-200 px-3 lg:flex">
              <div className="flex min-w-0 items-center gap-2.5">
                {branding.logo_url ? (
                  <img src={branding.logo_url} alt={sidebarLabel} className="h-10 w-10 shrink-0 rounded-lg object-contain" />
                ) : (
                  <div
                    className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-indigo-600 text-xs font-bold text-white"
                    style={{ backgroundColor: branding.primary_color || undefined }}
                  >
                    {sidebarInitials}
                  </div>
                )}
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold text-slate-800">{sidebarLabel}</p>
                  <p className="truncate text-[11px] font-medium text-slate-500">{currentTenantId}</p>
                </div>
              </div>
              <button
                type="button"
                onClick={toggleDesktopSidebar}
                className="rounded-lg p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                aria-label="Collapse sidebar"
                title="Collapse sidebar"
              >
                <PanelLeftClose size={18} />
              </button>
            </div>
            <nav className="sidebar-scroll flex min-h-0 flex-1 flex-col overflow-y-auto px-3 py-4">
              <div className="space-y-1">
                {pinnedTop.map((item) => renderNavLink(item))}
              </div>

              <div className="mt-1 space-y-1">
                {groupedNav.map(renderExpandedGroup)}
              </div>

              {pinnedBottom.length > 0 && (
                <div className="mt-auto space-y-1 border-t border-slate-200 pt-3">
                  {pinnedBottom.map((item) => renderNavLink(item))}
                </div>
              )}
            </nav>
          </div>

          <div className={`${desktopSidebarCollapsed ? 'hidden lg:flex' : 'hidden'} min-h-0 flex-1 flex-col`}>
            <div className="flex h-[61px] shrink-0 items-center justify-center border-b border-slate-200">
              <button
                type="button"
                onClick={toggleDesktopSidebar}
                className="rounded-xl p-2.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                aria-label="Expand sidebar"
                title="Expand sidebar"
              >
                <PanelLeftOpen size={19} />
              </button>
            </div>
            <nav className="sidebar-scroll flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto py-4">
              <div className="space-y-2">
                {pinnedTop.map((item) => renderNavLink(item, { compact: true }))}
              </div>

              <div className="space-y-2 border-y border-slate-200 py-3">
                {groupedNav.map((g) => renderRailGroupButton(g))}
              </div>

              {pinnedBottom.length > 0 && (
                <div className="mt-auto space-y-2 border-t border-slate-200 pt-3">
                  {pinnedBottom.map((item) => renderNavLink(item, { compact: true }))}
                </div>
              )}
            </nav>
          </div>
        </aside>

        {/* Tablet (md → lg): a permanent icon rail. Below md the phone tab bar
            does this job; from lg up the full sidebar is on screen. Tapping a
            group opens the drawer above with that group unfolded. */}
        <aside
          className="sticky top-0 z-30 hidden h-screen w-[76px] shrink-0 flex-col border-r border-slate-200 bg-white md:flex lg:hidden print:hidden"
          style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}
          data-testid="tablet-rail"
        >
          <div className="flex h-[61px] shrink-0 items-center justify-center border-b border-slate-200">
            <button
              type="button"
              onClick={() => setSidebarOpen(true)}
              className="rounded-xl p-2.5 text-slate-500 hover:bg-slate-100 hover:text-slate-800"
              aria-label="Open menu"
              title="Open menu"
            >
              <Menu size={22} />
            </button>
          </div>
          <nav className="sidebar-scroll flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto py-4">
            <div className="space-y-2">
              {pinnedTop.map((item) => renderNavLink(item, { compact: true }))}
            </div>
            <div className="space-y-2 border-y border-slate-200 py-3">
              {groupedNav.map((g) => renderRailGroupButton(g, openDrawerGroupFromRail))}
            </div>
            {pinnedBottom.length > 0 && (
              <div className="mt-auto space-y-2 border-t border-slate-200 pt-3">
                {pinnedBottom.map((item) => renderNavLink(item, { compact: true }))}
              </div>
            )}
          </nav>
        </aside>

        {/* Overlay for mobile */}
        {sidebarOpen && (
          <div
            className="fixed inset-0 z-40 bg-slate-900/50 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Content column: banner + top bar stick together above the page, so a
            published announcement stays on screen on every page while scrolling. */}
        <div className="flex min-h-screen min-w-0 flex-1 flex-col">
          {/* Safe-area padding keeps the bar clear of the notch / status bar when
              the app runs full-screen (installed app, iOS standalone). It is 0
              in an ordinary browser tab. */}
          <div data-app-topbar className="sticky top-0 z-40 bg-white print:static" style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}>
            <AnnouncementBanner />

            {/* Top Bar */}
            <div className="bg-white border-b border-slate-200">
              <div className="flex items-center justify-between gap-2 px-3 py-2.5 sm:px-4 sm:py-3">
                <div className="flex min-w-0 items-center gap-2 sm:gap-3">
                  <button
                    onClick={() => setSidebarOpen(!sidebarOpen)}
                    className="md:hidden p-2 hover:bg-slate-100 rounded-lg"
                    data-testid="mobile-menu-toggle"
                  >
                    {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
                  </button>
                  <div className="min-w-0 lg:hidden">
                    {branding.logo_url ? (
                      <img src={branding.logo_url} alt={sidebarLabel} className="h-8 max-w-[120px] object-contain" />
                    ) : (
                      <h1 className="truncate text-lg font-semibold sm:text-xl text-slate-800" style={{ color: branding.primary_color }}>
                        {sidebarLabel}
                      </h1>
                    )}
                  </div>
                </div>

                <div className="flex shrink-0 items-center gap-1.5 sm:gap-3 2xl:gap-4">
                  {/* A native floor screen parks its AI-suggestions chip here. */}
                  {nativeScreen && <div id={APPBAR_SLOT_ID} className="flex items-center empty:hidden" />}
                  <GlobalSearch />
                  {isEnabled('docs') && (
                    <button
                      type="button"
                      onClick={() => navigate(`/docs?context=${encodeURIComponent(normalized)}`)}
                      className="inline-flex items-center gap-1.5 rounded-lg border border-cyan-200 bg-cyan-50 px-2.5 py-1.5 text-xs font-medium text-cyan-800 hover:bg-cyan-100"
                      title="Open guidance for this screen"
                    >
                      <CircleHelp className="h-4 w-4" />
                      <span className="hidden md:inline">Help</span>
                    </button>
                  )}
                  <div className="hidden xl:inline-flex items-center rounded-full border border-indigo-200 bg-indigo-50 px-2.5 py-1 text-xs font-medium text-indigo-700">
                    Current Tenant: {currentTenantId}
                  </div>
                  {/* Screen-share switch. Shown only to logins that may see money at
                      all, because to everyone else it would advertise a control that
                      changes nothing they can observe. It only ever hides. */}
                  {mayShowMoney && (
                    <button
                      type="button"
                      onClick={toggleMoney}
                      className={`inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs font-medium ${
                        moneyHidden
                          ? 'border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100'
                          : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'
                      }`}
                      title={moneyHidden
                        ? 'Prices are hidden on this device. Click to show them again.'
                        : 'Hide every price on screen — for showing this to someone.'}
                    >
                      {moneyHidden ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      <span className="hidden lg:inline">{moneyHidden ? 'Prices hidden' : 'Prices'}</span>
                    </button>
                  )}
                  {notificationsVisible && <NotificationBell />}
                  <div className="text-right hidden md:block">
                    <p className="text-sm font-medium text-slate-800" data-testid="user-name">{user?.name}</p>
                    <p className="text-xs text-slate-500 capitalize" data-testid="user-role">{user?.role?.replace('_', ' ')}</p>
                  </div>
                  <Button
                    onClick={handleLogout}
                    variant="ghost"
                    size="sm"
                    className="gap-2"
                    data-testid="logout-button"
                  >
                    <LogOut size={18} />
                    <span className="hidden sm:inline">Logout</span>
                  </Button>
                </div>
              </div>
            </div>
          </div>

        {/* Main Content */}
        {/* Bottom padding on phones clears the tab bar (4rem) plus the home
            indicator, so the last row and a form's Save button are never under it. */}
        <main className={`ios-page-shell ${nativeScreen ? 'm3-page' : ''} min-w-0 flex-1 p-3 pb-[calc(5.5rem+env(safe-area-inset-bottom,0px))] sm:p-4 sm:pb-[calc(5.5rem+env(safe-area-inset-bottom,0px))] md:pb-4 lg:p-6`}>
          <Suspense fallback={
            <div className="flex items-center justify-center min-h-[200px]">
              <div className="animate-spin rounded-full h-10 w-10 border-2 border-slate-300 border-t-blue-600" />
            </div>
          }>
            <div key={location.pathname} className="ios-page-transition">
              {/* A suggestion board is rendered by the page that owns and can resolve
                  its rows. Keeping it out of the shell prevents an embedded page
                  from receiving a second, identical board above its own controls. */}
              {featureBlocked ? (
                normalized === '/' && visibleNav[0]?.path ? (
                  <Navigate to={visibleNav[0].path} replace />
                ) : (
                  <div className="flex flex-col items-center justify-center min-h-[200px] text-slate-600">
                    <p className="font-medium">This feature is not enabled for your organization.</p>
                    <p className="text-sm mt-1">Contact your administrator to enable it.</p>
                    <Link to="/" className="mt-4 text-blue-600 hover:underline">Go to Dashboard</Link>
                  </div>
                )
              ) : (
                <Outlet />
              )}
            </div>
          </Suspense>
        </main>
        </div>
      </div>

      {/* Any save in the app can be held for a manager's approval, so the
          explanation is mounted once here rather than wired into each page —
          the pages nobody remembered to wire are exactly the ones that would
          fail as a bare red toast. */}
      <SupervisionHoldDialog />
      <StepTimesDialog />
      {/* Keeps the maker and the approvers in step: re-reads approval lists and
          toasts a decision without anybody refreshing the page. */}
      {user && isEnabled('supervision') && <SupervisionPulse />}

      {user && (
        <MobileBottomNav
          items={visibleNav}
          isItemActive={isItemActive}
          iconMap={iconMap}
          userKey={`${currentTenantId}:${user.id || user.email || 'me'}`}
          onMenu={() => setSidebarOpen((v) => !v)}
          menuOpen={sidebarOpen}
          notificationsVisible={notificationsVisible}
          unreadNotifications={unreadNotifications}
        />
      )}

      {/* Floats above every page: a question is asked where the operator already
          is, not on a dashboard they would have to navigate back to. */}
      <AssistantWidget />
    </div>
    </NavigationProvider>
  );
};

export default Layout;
